import { StrategyDetailsPageGetDocumentRecordQuery } from '@/__generated__/graphql';
import client from '@/apollo-client';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import { AiAssistantIcon } from '@/assets/static/icons/ai-assistant';
import { OpenFileIcon } from '@/assets/static/icons/open-file';
import { UploadFileIcon } from '@/assets/static/icons/upload-file';
import { AiAssistant, aiPaneTabsOnEditFieldPages } from '@/components/aiAssistant';
import { IAiAssets, IAiRunPromptAnswerCitation } from '@/components/aiAssistant/types';
import { Accordion } from '@/components/basicComponents/accordion';
import ActionDropdown from '@/components/basicComponents/actionDropdown';
import Button from '@/components/basicComponents/button';
import FileUpload from '@/components/basicComponents/fileUpload';
import { ISelectValue } from '@/components/basicComponents/select';
import Tag from '@/components/basicComponents/tag';
import { IOfferingMaterialsAttachments } from '@/components/dealCatalog-page/types';
import { isImageExtension } from '@/components/dueDiligence-page/dueDiligenceRecord/utils';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitleWrap } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { GET_ASSET_SIGNED_URL_QUERY } from '@/components/settings-page/queries';
import { DealDocumentsModalPage } from '@/components/strategyDetails-page/editStrategyDescriptionPage/dealDocumentsModal-page';
import DeleteAttachment from '@/components/strategyDetails-page/editStrategyDescriptionPage/modals/deleteAttachment';
import DropdownActions from '@/components/table/dropdownActions';
import { FieldEditorModalPage } from '@/features/fieldEditorModalPages/FieldEditorModalPage';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useGoBack } from '@/hooks/useGoBack';
import { IFieldEditor } from '@/shared/types/fieldEditor';
import { convertDocumentRecordFieldToFieldEditor, FieldList, hasAiAnswer } from '@/shared/ui/fieldList';
import { dateFormatter } from '@/shared/utils/date';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { extractAndFormatDate, extractAndFormatDollar, extractAndFormatNumber, extractAndFormatPercentage } from '@/shared/utils/fieldEditor';
import guid from '@/shared/utils/guid';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { gql, NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { AIFieldType, DocumentRecordSentimentFlag, DocumentRecordStatus, TenantType } from '@trace-8/trace8-schema';
import axios from 'axios';
import { format, isValid as isValidDate } from 'date-fns';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { MANAGER_UPDATES_EDITABLE_ROLES } from '../../../../managerUpdates-page/constants';
import { getTagStyleType } from '../../../../managerUpdates-page/utils';
import PublishUpdate from '../modals/publishUpdate';
import UnpublishUpdate from '../modals/unpublishUpdate';
import {
  STRATEGY_DETAILS_PAGE_DELETE_DOCUMENT_FIELD_ANSWER_ATTACHMENT_MUTATION,
  STRATEGY_DETAILS_PAGE_GET_DOCUMENT_RECORD_QUERY,
  STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_FIELD_ANSWER_MUTATION,
  STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_RECORD_MUTATION,
  STRATEGY_DETAILS_PAGE_UPDATE_INVESTMENT_OFFERING_MATERIALS_ATTACHMENT_MUTATION
} from '../queries';
import { EditBasicInfo, IBasicInfoSectionData } from './editBasicInfo';

const GET_INVESTMENT_OFFERING_MATERIALS_ATTACHMENTS_QUERY = gql(`
  query EditManagerUpdateInvestment($id: String!) {
    Investment(id: $id) {
      id
      name
      offeringMaterialsAttachments {
        id
        asset {
          url
          assetKey
        }
        visibility
      }
    }
  }
`);

const getStatusTagStyleType = (status: string) => {
  switch (status) {
    case 'Draft':
      return 'neutral';
    case 'Published':
      return 'valid';
    default:
      return 'default';
  }
};

const showZeroStateInBasicInfoSection = (basicInfoSectionData: IBasicInfoSectionData | null): boolean => {
  if (!basicInfoSectionData) return true;

  const { name, publishDate, sentimentFlag, sentimentFlagLabel } = basicInfoSectionData;

  const isNameEmpty = name?.value === null && (!name?.AI?.answers || name.AI.answers.length === 0);
  const isPublishDateEmpty = publishDate?.value === null && (!publishDate?.AI?.answers || publishDate.AI.answers.length === 0);
  const isSentimentEmpty = !sentimentFlag && !sentimentFlagLabel;

  return isNameEmpty && isPublishDateEmpty && isSentimentEmpty;
};

const hasAiAnswersInBasicInfoSection = (basicInfoSectionData: IBasicInfoSectionData | null) => {
  return (
    (basicInfoSectionData?.name?.value === null && basicInfoSectionData?.name.AI?.answers.length) ||
    (basicInfoSectionData?.publishDate?.value === null && basicInfoSectionData?.publishDate.AI?.answers.length)
  );
};

const getBasicInfoSectionFieldValue = (field: StrategyDetailsPageGetDocumentRecordQuery['getDocumentRecord']['fields'][number] | null) => {
  if (!field) return '';

  const isDateField = field.documentField?.type === getEnumKeyByValue(AIFieldType, AIFieldType.DATE);
  const aiAnswer = field.AI?.answers?.[0];

  if (field.value === null && aiAnswer) {
    if (isDateField) {
      const formattedDate = extractAndFormatDate(aiAnswer);
      if (formattedDate && isValidDate(new Date(formattedDate))) {
        return dateFormatter(format(new Date(formattedDate), 'yyyy-MM-dd'), 'yyyy-MM-dd');
      }
      return formattedDate || '';
    }
    return aiAnswer || '';
  }

  if (isDateField && field.value) {
    const date = new Date(field.value);
    if (isValidDate(date)) {
      return dateFormatter(format(date, 'yyyy-MM-dd'), 'yyyy-MM-dd');
    }
    return '';
  }

  return field.value || '';
};

const visibilityOptions: ISelectValue[] = [
  { label: 'Never Visible to Advisors', value: 'Never Visible to Advisors' },
  { label: 'Visible to Advisors', value: 'Visible to Advisors' }
];

const getDataForUpdate = (row: IFieldEditor): { value: string; commentary: string } => {
  switch (row.documentFieldType) {
    case getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT):
      return {
        value: row.AI.answers[0],
        commentary: null
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.SHORT_TEXT):
      return {
        value: row.AI.answers[0],
        commentary: null
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.NUMBER):
      return {
        value: extractAndFormatNumber(row.AI.answers[0]),
        commentary: row.AI.answers[0]
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.DOLLAR):
      return {
        value: extractAndFormatDollar(row.AI.answers[0]),
        commentary: row.AI.answers[0]
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.PERCENTAGE):
      return {
        value: extractAndFormatPercentage(row.AI.answers[0]),
        commentary: row.AI.answers[0]
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.DATE):
      return {
        value: extractAndFormatDate(row.AI.answers[0]),
        commentary: row.AI.answers[0]
      };
    default:
      return {
        value: '',
        commentary: ''
      };
  }
};

type BasicInfoField = StrategyDetailsPageGetDocumentRecordQuery['getDocumentRecord']['fields'][number];

const shouldShowAcceptButtonForBasicInfoField = (field: BasicInfoField | null | undefined): boolean => {
  if (!field) return false;
  return field.value === null && field.AI?.answers?.length > 0;
};

const shouldShowClearButtonForBasicInfoField = (field: BasicInfoField | null | undefined): boolean => {
  if (!field) return false;
  return (field.AI?.answers?.length > 0 && field.value !== '' && field.commentary !== '') || (field.value !== null && field.commentary !== null);
};

export const EditManagerUpdate = () => {
  const { isMobile, isTablet } = useResponsive();
  const goBack = useGoBack();
  const params = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const backToTitle = searchParams.get('backToTitle') || '';

  const [documentRecordData, setDocumentRecordData] = useState<StrategyDetailsPageGetDocumentRecordQuery['getDocumentRecord'] | null>(null);
  const [basicInfoSectionData, setBasicInfoSectionData] = useState<IBasicInfoSectionData | null>(null);
  const [citationsTabData, setCitationsTabData] = useState<
    {
      id: string;
      fieldName: string;
      citations: IAiRunPromptAnswerCitation[];
    }[]
  >([]);
  const [canUserEdit, setCanUserEdit] = useState(false);
  const [editFieldPageType, setEditFieldPageType] = useState('default');
  const [scrollTo, setScrollTo] = useState(0);
  const [currentField, setCurrentField] = useState<IFieldEditor | null>(null);
  const [allFields, setAllFields] = useState<IFieldEditor[] | null>(null);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState(false);
  const [generateAnswersLoading, setGenerateAnswersLoading] = useState(false);
  const [isGenerateAnswersLoadingCompleted, setIsGenerateAnswersLoadingCompleted] = useState(false);
  const [allUploadedFiles, setAllUploadedFiles] = useState<IOfferingMaterialsAttachments[] | []>([]);
  const [currentAttachment, setCurrentAttachment] = useState<IOfferingMaterialsAttachments | null>(null);
  const [filesForAiAssistant, setFilesForAiAssistant] = useState<IAiAssets[]>([]);
  const [aiRunStatusRefetch, setAiRunStatusRefetch] = useState<{ getAIRunStatusRefetch: () => void }>({ getAIRunStatusRefetch: () => {} });

  const [updateDocumentFieldAnswer] = useMutation(STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_FIELD_ANSWER_MUTATION);
  const [updateDocumentRecord] = useMutation(STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_RECORD_MUTATION);
  const [deleteAttachment] = useMutation(STRATEGY_DETAILS_PAGE_DELETE_DOCUMENT_FIELD_ANSWER_ATTACHMENT_MUTATION);
  const [updateAttachment] = useMutation(STRATEGY_DETAILS_PAGE_UPDATE_INVESTMENT_OFFERING_MATERIALS_ATTACHMENT_MUTATION);

  const { refetch: documentRecordRefetch, networkStatus: documentRecordNetworkStatus } = useQuery(STRATEGY_DETAILS_PAGE_GET_DOCUMENT_RECORD_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    skip: !params?.updateId,
    variables: {
      id: params?.updateId
    },
    onCompleted: (data) => {
      const { name, fields, sentimentFlag, sentimentFlagLabel } = data?.getDocumentRecord || {};

      const filteredManagerUpdateFields = fields?.filter(
        (field) => field.documentField.name !== 'Document Record Name' && field.documentField.name !== 'Document Record Publish Date'
      );

      const allEditFields: IFieldEditor[] = convertDocumentRecordFieldToFieldEditor(filteredManagerUpdateFields);
      setAllFields(allEditFields);

      const updatedDocumentRecordFields = {
        ...data.getDocumentRecord,
        fields: filteredManagerUpdateFields
      };
      setDocumentRecordData(updatedDocumentRecordFields);

      const basicInfoNameField = fields?.find((field) => field.documentField.name === 'Document Record Name');
      const basicInfoPublishDateField = fields?.find((field) => field.documentField.name === 'Document Record Publish Date');
      setBasicInfoSectionData({
        name: basicInfoNameField
          ? {
              ...basicInfoNameField,
              value: basicInfoNameField?.value === null && !basicInfoNameField?.AI?.answers?.length ? name : basicInfoNameField?.value
            }
          : null,
        publishDate: basicInfoPublishDateField || null,
        sentimentFlag: (sentimentFlag as DocumentRecordSentimentFlag) || null,
        sentimentFlagLabel
      });
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { networkStatus: investmentAttachmentsNetworkStatus, refetch: investmentAttachmentsRefetch } = useQuery<{
    Investment: { id: string; name: string; offeringMaterialsAttachments: IOfferingMaterialsAttachments[] };
  }>(GET_INVESTMENT_OFFERING_MATERIALS_ATTACHMENTS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: !params?.strategyId,
    variables: {
      id: params?.strategyId
    },
    onCompleted: (data) => {
      const currentDocumentRecordAssetKey = `investments/${params?.strategyId}/mu/${params?.updateId}`;
      const filteredAttachments =
        data?.Investment?.offeringMaterialsAttachments?.filter(
          (attachment) => attachment?.asset?.assetKey?.includes(currentDocumentRecordAssetKey)
        ) || [];

      setAllUploadedFiles(filteredAttachments);
    }
  });

  useEffect(() => {
    if (MANAGER_UPDATES_EDITABLE_ROLES.some((role) => user.roles.includes(role)) && user.tenant.type === TenantType.PLATFORM_MANAGER) {
      setCanUserEdit(true);
      return;
    }
  }, [user]);

  useEffect(() => {
    if (editFieldPageType === 'default' && scrollTo) {
      window.scrollTo(0, scrollTo);
      setScrollTo(0);
    }
  }, [editFieldPageType]);

  useEffect(() => {
    const { openAiPane } = location.state || {};

    if (openAiPane) {
      setIsAiAssistantOpen(true);
      navigate('.', { replace: true, state: null });
    }
  }, [location.state]);

  useEffect(() => {
    if (editFieldPageType === 'basic-info') {
      const { name, publishDate } = basicInfoSectionData || {};
      if (!name || !publishDate) {
        setCitationsTabData([]);
        return;
      }
      setCitationsTabData([
        { id: name?.documentField?.id, fieldName: 'Name', citations: name?.AI?.citations ?? [] },
        { id: publishDate?.documentField?.id, fieldName: 'Publish Date', citations: publishDate?.AI?.citations ?? [] }
      ]);
      return;
    }
    setCitationsTabData([{ id: currentField?.id, fieldName: currentField?.name, citations: currentField?.AI?.citations ?? [] }]);
  }, [currentField, editFieldPageType]);

  const openAdvancedOptions = () => {
    setIsAiAssistantOpen(false);
    setEditFieldPageType('advanced-options');
  };

  const handleGenerateAnswersLoadingChange = (loadingStatus: boolean, showCompleteIcon: boolean) => {
    setGenerateAnswersLoading(loadingStatus);
    setIsGenerateAnswersLoadingCompleted(showCompleteIcon);
  };

  const handleFilesForAiAssistant = (files: IAiAssets[]) => {
    setFilesForAiAssistant(files);
  };

  const openDealDocumentsPage = (attachment?: IOfferingMaterialsAttachments) => {
    setScrollTo(window.scrollY);
    setEditFieldPageType('edit-attachment');
    if (attachment) {
      setCurrentAttachment(attachment);
    }
  };

  const onEditField = (fieldItem: IFieldEditor) => {
    setScrollTo(window.scrollY);
    setEditFieldPageType(fieldItem.documentFieldType);
    const editField = allFields?.find((field) => field.id === fieldItem.id);
    setCurrentField(editField || null);
    documentRecordRefetch();
  };

  const closeEditPage = () => {
    setEditFieldPageType('default');
    setCurrentField(null);
    documentRecordRefetch();
  };

  const openModalWindow = (type: string, attachment?: IOfferingMaterialsAttachments) => {
    setModalWindow({ type, isOpen: true });
    if (attachment) {
      setCurrentAttachment(attachment);
    }
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
    setCurrentAttachment(null);
  };

  const openAttachment = async (assetKey: string) => {
    try {
      const { data } = await client.query({
        fetchPolicy: 'network-only',
        query: GET_ASSET_SIGNED_URL_QUERY,
        variables: {
          assetKey
        }
      });
      window.open(`${data.getAssetSignedUrl}`, '_blank');
    } catch (error) {
      console.error(error);
    }
  };

  const handleAiRunStatusRefetch = (getAIRunStatusRefetch: () => void) => {
    setAiRunStatusRefetch({ getAIRunStatusRefetch });
  };

  const triggerAiRunStatusRefetch = () => {
    aiRunStatusRefetch.getAIRunStatusRefetch();
  };

  const addNewAttachments = async (file: File, visibility: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('visibility', visibility);

    await axios.post(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/investment-offering-materials-attachment/investment/${params?.strategyId}/document-record/${params?.updateId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );

    await investmentAttachmentsRefetch();
  };

  const updateAttachmentVisibility = async (visibility: string) => {
    if (currentAttachment.visibility !== visibility) {
      await updateAttachment({
        variables: {
          data: {
            id: currentAttachment.id,
            visibility: visibility
          }
        },
        onCompleted: () => {
          setAllUploadedFiles((prev) =>
            prev.map((file) => {
              if (file.id === currentAttachment.id) {
                return { ...file, visibility };
              }
              return file;
            })
          );
        }
      });
    }
  };

  const onFileUploadComplete = async () => {
    await investmentAttachmentsRefetch();
  };

  const handleUpdateFieldAnswer = async ({
    id,
    recordId,
    value,
    commentary
  }: {
    id: string;
    recordId: string;
    value: string;
    commentary?: string;
  }) => {
    await updateDocumentFieldAnswer({
      variables: {
        data: {
          fieldId: id,
          recordId: recordId,
          value,
          commentary
        }
      }
    });
  };

  const handleDeleteAttachment = async ({ id }: { id: string }) => {
    await deleteAttachment({
      variables: {
        id
      }
    });
  };

  const handleUploadAttachment = async ({
    file,
    recordId,
    fieldId,
    fieldAnswerId
  }: {
    file: File;
    recordId: string;
    fieldId: string;
    fieldAnswerId: string;
  }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('includeInAutomatedContent', 'true');

    const { data: attachmentData } = await axios.post(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/document-field-answer/document-record/${recordId}/document-field-answer/${fieldId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );

    const { assetUrl, assetKey } = attachmentData || {};

    return { id: guid(), url: assetUrl, assetKey: assetKey || '' };
  };

  const onAcceptAiAnswer = async (field: IFieldEditor) => {
    const { value, commentary } = getDataForUpdate(field);

    await updateDocumentFieldAnswer({
      variables: {
        data: {
          fieldId: field.fieldId,
          recordId: field.recordId,
          value,
          commentary
        }
      }
    });

    await documentRecordRefetch();
  };

  const onAcceptBasicInfoFieldsAiAnswer = async () => {
    try {
      const { name, publishDate } = basicInfoSectionData || {};

      if (name) {
        await updateDocumentFieldAnswer({
          variables: {
            data: {
              fieldId: name?.fieldId,
              recordId: name?.recordId,
              value: name?.AI?.answers?.[0]
            }
          }
        });
      }

      if (publishDate) {
        await updateDocumentFieldAnswer({
          variables: {
            data: {
              fieldId: publishDate?.fieldId,
              recordId: publishDate?.recordId,
              value: extractAndFormatDate(publishDate?.AI?.answers?.[0])
            }
          }
        });
      }

      await documentRecordRefetch();
    } catch (error) {
      console.error(error);
    }
  };

  const onClearField = async (field: IFieldEditor) => {
    const commentary =
      hasAiAnswer(field) &&
      field.documentFieldType !== getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT) &&
      field.documentFieldType !== getEnumKeyByValue(AIFieldType, AIFieldType.SHORT_TEXT)
        ? ''
        : null;

    await updateDocumentFieldAnswer({
      variables: {
        data: {
          fieldId: field.fieldId,
          recordId: field.recordId,
          value: field?.AI?.answers?.length ? '' : null,
          commentary
        }
      }
    });

    await documentRecordRefetch();
  };

  const onClearBasicInfoFields = async () => {
    try {
      const { name, publishDate } = basicInfoSectionData || {};
      const { recordId } = name || publishDate || {};

      await updateDocumentFieldAnswer({
        variables: {
          data: {
            recordId,
            fieldId: name.fieldId,
            value: name?.AI?.answers?.length ? '' : null
          }
        }
      });

      await updateDocumentFieldAnswer({
        variables: {
          data: {
            recordId,
            fieldId: publishDate.fieldId,
            value: publishDate?.AI?.answers?.length ? '' : null
          }
        }
      });

      await updateDocumentRecord({
        variables: {
          data: {
            id: recordId,
            sentimentFlag: null,
            sentimentFlagLabel: null
          }
        }
      });

      await documentRecordRefetch();
    } catch (error) {
      console.error(error);
    }
  };

  const dropdownActions = () => {
    return [
      ...(documentRecordData?.status === 'Draft'
        ? [
            {
              name: 'Publish Update',
              onClick: () => openModalWindow('publish-update')
            }
          ]
        : []),
      ...(documentRecordData?.status === 'Published'
        ? [
            {
              name: 'Unpublish Update',
              onClick: () => openModalWindow('unpublish-update')
            }
          ]
        : [])
    ];
  };

  const isLoading =
    investmentAttachmentsNetworkStatus === NetworkStatus.loading ||
    investmentAttachmentsNetworkStatus === NetworkStatus.setVariables ||
    documentRecordNetworkStatus === NetworkStatus.loading ||
    documentRecordNetworkStatus === NetworkStatus.setVariables;

  if (isLoading) {
    return <LoaderOnWholeScreen />;
  }

  if (editFieldPageType === 'edit-attachment') {
    return (
      <DealDocumentsModalPage
        currentAttachment={
          currentAttachment
            ? {
                assetKey: currentAttachment?.asset?.assetKey || '',
                url: currentAttachment?.asset?.url || '',
                visibility: currentAttachment?.visibility || ''
              }
            : null
        }
        visibilityOptions={visibilityOptions}
        addNewAttachments={addNewAttachments}
        updateAttachmentVisibility={updateAttachmentVisibility}
        handleClosePage={() => {
          setEditFieldPageType('default');
          setCurrentAttachment(null);
        }}
      />
    );
  }

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'delete-attachment' && (
        <DeleteAttachment
          isOpen={modalWindow.isOpen}
          currentAttachment={currentAttachment}
          recordId={documentRecordData?.id ?? ''}
          notVisibleTitle="Never Visible to Advisors"
          triggerAiRunStatusRefetch={triggerAiRunStatusRefetch}
          documentRecordRefetch={documentRecordRefetch}
          setUploadedFiles={setAllUploadedFiles}
          setFilesForAiAssistant={setFilesForAiAssistant}
          onClose={closeModalWindow}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'publish-update' && (
        <PublishUpdate
          isOpen={modalWindow.isOpen}
          publishDate={getBasicInfoSectionFieldValue(basicInfoSectionData?.publishDate)}
          updateId={documentRecordData?.id ?? null}
          ownerEmail={documentRecordData?.owner?.email ?? null}
          refetch={documentRecordRefetch}
          onClose={closeModalWindow}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'unpublish-update' && (
        <UnpublishUpdate
          isOpen={modalWindow.isOpen}
          onClose={closeModalWindow}
          updateId={documentRecordData?.id ?? null}
          refetch={documentRecordRefetch}
        />
      )}
      <EditManagerUpdatesWrapper>
        {((!isMobile && !isTablet) || !isAiAssistantOpen) && editFieldPageType === 'basic-info' && (
          <EditBasicInfo
            basicInfoSectionData={basicInfoSectionData}
            aiButtonConfig={{
              isLoading: generateAnswersLoading,
              secondaryIcon: isGenerateAnswersLoadingCompleted && <ApprovedIcon width={20} height={20} fill={themeVar('context.light')} />,
              onClick: () => setIsAiAssistantOpen(!isAiAssistantOpen)
            }}
            refetch={documentRecordRefetch}
            handleClose={() => setEditFieldPageType('default')}
          />
        )}
        {/* {((!isMobile && !isTablet) || !isAiAssistantOpen) && editFieldPageType === 'advanced-options' && (
          <AdvancedOptions
            ids={{
              recordId: documentRecordData?.id,
              investmentId: params?.strategyId
            }}
            filesForAiAssistant={filesForAiAssistant}
            handleFilesForAiAssistant={handleFilesForAiAssistant}
            handleGenerateAnswersLoadingChange={handleGenerateAnswersLoadingChange}
            mainQueryRefetch={documentRecordRefetch}
            handleClose={() => setEditFieldPageType('default')}
          />
        )} */}
        {((!isMobile && !isTablet) || !isAiAssistantOpen) &&
          editFieldPageType !== 'default' &&
          editFieldPageType !== 'basic-info' &&
          editFieldPageType !== 'edit-attachment' && (
            <FieldEditorModalPage
              allFields={allFields}
              currentField={currentField}
              investmentId={params?.strategyId ?? ''}
              editFieldPageType={editFieldPageType as keyof typeof AIFieldType}
              setIsAiPaneOpen={setIsAiAssistantOpen}
              setCurrentField={setCurrentField}
              setEditFieldPageType={setEditFieldPageType}
              mainQueryRefetch={documentRecordRefetch}
              handleUpdateFieldAnswer={handleUpdateFieldAnswer}
              handleUploadAttachment={handleUploadAttachment}
              handleDeleteAttachment={handleDeleteAttachment}
              handleClose={closeEditPage}
            />
          )}
        {((!isMobile && !isTablet) || !isAiAssistantOpen) && editFieldPageType === 'default' && (
          <div style={{ width: '100%' }}>
            <MainWrap>
              <Header
                aiButtonConfig={{
                  isLoading: generateAnswersLoading,
                  secondaryIcon: isGenerateAnswersLoadingCompleted && <ApprovedIcon width={20} height={20} fill={themeVar('context.light')} />,
                  onClick: () => setIsAiAssistantOpen(!isAiAssistantOpen)
                }}
                modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/managerUpdates' })} backToTitle={backToTitle} />}
              />
              <PageTitleWrap withBorder>
                Edit Update
                {dropdownActions().length && (
                  <DropdownActionsWrapper>
                    <Tag
                      title={documentRecordData?.status}
                      styleType={getStatusTagStyleType(documentRecordData?.status)}
                      tooltip={
                        documentRecordData?.status === getEnumKeyByValue(DocumentRecordStatus, DocumentRecordStatus.Published) &&
                        basicInfoSectionData?.publishDate?.value &&
                        isValidDate(new Date(basicInfoSectionData?.publishDate?.value))
                          ? `Published on ${dateFormatter(format(new Date(basicInfoSectionData?.publishDate?.value), 'yyyy-MM-dd'), 'yyyy-MM-dd')}`
                          : null
                      }
                    />
                    <ActionDropdown options={dropdownActions()} size="sm" styleType="outline" />
                  </DropdownActionsWrapper>
                )}
              </PageTitleWrap>
            </MainWrap>
            <MainWrap>
              <PaddingWrap>
                <SectionsWrap>
                  <Accordion title="Basic Info">
                    {!!showZeroStateInBasicInfoSection(basicInfoSectionData) ? (
                      <SectionZeroState>
                        <SectionZeroStateText>No information has been added yet.</SectionZeroStateText>
                        <Button
                          onClick={() => setEditFieldPageType('basic-info')}
                          styleType="outline"
                          icon={<AddButtonIcon fill={themeVar('action.default')} />}
                          size="md"
                          width="max-content"
                        >
                          Add Information
                        </Button>
                      </SectionZeroState>
                    ) : (
                      <div>
                        <BasicInfoFieldsWrapper isMobile={isMobile}>
                          {!!hasAiAnswersInBasicInfoSection(basicInfoSectionData) && (
                            <MagicWandIconWrapper>
                              <AiAssistantIcon width={28} height={28} fill={themeVar('font.weak')} />
                            </MagicWandIconWrapper>
                          )}
                          <BasicInfoFields>
                            {!!hasAiAnswersInBasicInfoSection(basicInfoSectionData) && (
                              <TwoColumnWrapper isMobile={isMobile}>
                                <BasicInfoItem>
                                  <b>Suggested:</b>
                                </BasicInfoItem>
                              </TwoColumnWrapper>
                            )}
                            <TwoColumnWrapper isMobile={isMobile}>
                              <BasicInfoItem>Name</BasicInfoItem>
                              {!!getBasicInfoSectionFieldValue(basicInfoSectionData?.name) && (
                                <BasicInfoItem isBold>{getBasicInfoSectionFieldValue(basicInfoSectionData?.name)}</BasicInfoItem>
                              )}
                            </TwoColumnWrapper>
                            <TwoColumnWrapper isMobile={isMobile}>
                              <BasicInfoItem>Publish Date</BasicInfoItem>
                              {!!getBasicInfoSectionFieldValue(basicInfoSectionData?.publishDate) && (
                                <BasicInfoItem isBold>{getBasicInfoSectionFieldValue(basicInfoSectionData?.publishDate)}</BasicInfoItem>
                              )}
                            </TwoColumnWrapper>
                            <TwoColumnWrapper isMobile={isMobile}>
                              <BasicInfoItem>Flag</BasicInfoItem>
                              {basicInfoSectionData?.sentimentFlagLabel && (
                                <BasicInfoItem isBold>
                                  <Tag
                                    title={basicInfoSectionData?.sentimentFlagLabel}
                                    styleType={getTagStyleType(basicInfoSectionData?.sentimentFlag)}
                                    size="md"
                                  />
                                </BasicInfoItem>
                              )}
                            </TwoColumnWrapper>
                          </BasicInfoFields>
                          {(shouldShowAcceptButtonForBasicInfoField(basicInfoSectionData?.name) ||
                            shouldShowAcceptButtonForBasicInfoField(basicInfoSectionData?.publishDate)) && (
                            <AcceptAiAnswerButton id="acceptAiAnswerButton" onClick={onAcceptBasicInfoFieldsAiAnswer}>
                              <ApprovedIcon width={23} height={23} fill={themeVar('font.action')} />
                            </AcceptAiAnswerButton>
                          )}
                          <ActionDropdown
                            options={[
                              { name: 'Edit', onClick: () => setEditFieldPageType('basic-info') },
                              ...(shouldShowClearButtonForBasicInfoField(basicInfoSectionData?.name) ||
                              shouldShowClearButtonForBasicInfoField(basicInfoSectionData?.publishDate)
                                ? [{ name: 'Clear', onClick: onClearBasicInfoFields }]
                                : [])
                            ]}
                          />
                        </BasicInfoFieldsWrapper>
                      </div>
                    )}
                  </Accordion>
                  <Accordion title="Update">
                    <FieldList
                      fields={allFields}
                      mode="edit"
                      onEditField={onEditField}
                      onAcceptAiAnswer={onAcceptAiAnswer}
                      onClearField={onClearField}
                    />
                  </Accordion>
                  <Accordion title="Attachments">
                    {allUploadedFiles.length ? (
                      <>
                        <PreviewWrapper>
                          {allUploadedFiles.map((item) => (
                            <FileUpload
                              key={item.id}
                              type={isImageExtension(item.asset.assetKey) ? 'image' : 'document'}
                              imageSrc={item.asset.url}
                              fileName={item.asset.assetKey.split('/').pop() || ''}
                              withHover
                              additionalContent={
                                <Tag title={item.visibility} styleType={item.visibility === 'Visible to Advisors' ? 'valid' : 'neutral'} size="md" />
                              }
                              customActionButtons={
                                <>
                                  <OpenAttachmentWrapper id="infoWrap" onClick={() => openAttachment(item.asset.assetKey)}>
                                    <OpenFileIcon fill={themeVar('font.action')} />
                                  </OpenAttachmentWrapper>
                                  <DropdownActions
                                    items={[
                                      { name: 'Edit', onClick: () => openDealDocumentsPage(item) },
                                      { name: 'Delete', onClick: () => openModalWindow('delete-attachment', item) }
                                    ]}
                                  />
                                </>
                              }
                            />
                          ))}
                        </PreviewWrapper>
                        <Button
                          onClick={() => openDealDocumentsPage()}
                          styleType="outline"
                          size="md"
                          width="max-content"
                          icon={<UploadFileIcon width={18} height={23} fill={themeVar('font.action')} />}
                        >
                          Upload
                        </Button>
                      </>
                    ) : (
                      <SectionZeroState>
                        <SectionZeroStateText>No files have been added yet.</SectionZeroStateText>
                        <Button
                          onClick={() => openDealDocumentsPage()}
                          styleType="outline"
                          icon={<UploadFileIcon width={18} height={23} fill={themeVar('action.default')} />}
                          size="md"
                          width="max-content"
                        >
                          Upload
                        </Button>
                      </SectionZeroState>
                    )}
                  </Accordion>
                </SectionsWrap>
              </PaddingWrap>
            </MainWrap>
          </div>
        )}
        <AiAssistant
          isAiAssistantOpen={isAiAssistantOpen}
          ids={{
            recordId: documentRecordData?.id,
            investmentId: params?.strategyId
          }}
          type="mu"
          filesForAiAssistant={filesForAiAssistant}
          tabs={editFieldPageType !== 'default' && editFieldPageType !== 'advanced-options' ? aiPaneTabsOnEditFieldPages : []}
          citationsTabData={citationsTabData}
          onFileUploadComplete={onFileUploadComplete}
          handleFilesForAiAssistant={handleFilesForAiAssistant}
          handleAiRunStatusRefetch={handleAiRunStatusRefetch}
          handleGenerateAnswersLoadingChange={handleGenerateAnswersLoadingChange}
          mainQueryRefetch={documentRecordRefetch}
          openAdvancedOptions={openAdvancedOptions}
          onClose={() => setIsAiAssistantOpen(false)}
        />
      </EditManagerUpdatesWrapper>
    </>
  );
};

const DropdownActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;

  #infoWrap {
    opacity: 1;
  }
`;

const EditManagerUpdatesWrapper = styled.div`
  display: flex;
  justify-content: center;
  transition: all 0.3s;
`;

const SectionsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 50px 0;
`;

const SectionZeroState = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;
`;

const SectionZeroStateText = styled.div`
  padding: 10px 0;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  color: ${themeVar('font.weak')};
`;

const PreviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OpenAttachmentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 40px;
  width: 40px;
  opacity: 0;
  border-radius: 4px;
  border: 1px solid ${themeVar('font.action')};
  background: ${themeVar('layer.base')};
`;

const BasicInfoFieldsWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 28px 12px;

  & #actionDropdown,
  & #acceptAiAnswerButton {
    opacity: ${({ isMobile }) => (isMobile ? '1' : '0')};
  }

  &:hover #actionDropdown,
  &:hover #acceptAiAnswerButton {
    opacity: 1;
  }

  &:hover {
    background-color: ${themeVar('layer.2')};
    transition: background-color 100ms ease;
  }
`;

const BasicInfoFields = styled.div`
  padding-left: 20px;
  width: 100%;
`;

const MagicWandIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 10px;
  border-right: 1px solid ${themeVar('border.base')};
  align-self: stretch;
`;

const AcceptAiAnswerButton = styled.div`
  background-color: ${themeVar('layer.base')};
  border: 1px solid ${themeVar('font.action')};
  border-radius: 4px;
  height: 40px;
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const TwoColumnWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 20px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '60%')};
  padding: 2px 10px;
`;

const BasicInfoItem = styled.div<{ isBold?: boolean }>`
  font-size: 16px;
  font-weight: ${({ isBold }) => (isBold ? 600 : 400)};
  line-height: 22.4px;
  color: ${themeVar('font.base')};
  padding: 4px;
  width: max-content;
`;
